import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import AppDateField from '@/components/AppDateField';
import { useToast } from '@/hooks/use-toast';
import { useApp } from '@/contexts/AppContext';
import type { CalendarItem } from '@/hooks/useCalendarItems';
import { upsertAdultingCalendarItem, isGoogleCalendarConfigured } from '@/lib/google-calendar';
import { loadIntegrationSettings, saveIntegrationSettings } from '@/lib/integration-settings';
import type { CalendarEvent } from '@/types';
import {
  AppBottomSheet,
  AppBottomSheetHeader,
  AppButton,
  AppButtonRow,
  AppFieldGroup,
  AppInput,
  AppText,
} from '../../ui-foundation';
import { AppSwitch } from '../../ui-foundation/adapters/web';
import {
  REMINDER_PRESETS,
  REMINDER_STRENGTH_OPTIONS,
  buildQuickReminderSubtitle,
  getReminderStrengthDescription,
  parseQuickReminderInput,
  type ReminderPreset,
  type ReminderRecurrence,
  type ReminderStrength,
} from '../../ui-foundation/features/notifications';

type QuickReminderComposerContextValue = {
  openQuickReminder: (options?: { preset?: ReminderPreset | null }) => void;
};

const QuickReminderComposerContext = createContext<QuickReminderComposerContextValue | null>(null);

function buildMonthlyRecurrence(date: string): ReminderRecurrence {
  const anchor = new Date(`${date}T09:00:00`);
  return {
    recurrenceType: 'custom',
    recurrenceInterval: 1,
    recurrenceUnit: 'months',
    recurrenceDayOfWeek: null,
    recurrenceWeekOfMonth: null,
    recurrenceMonthDay: anchor.getDate(),
    summary: 'Repeats every month',
  };
}

function buildQuickReminderCalendarItem(event: CalendarEvent): CalendarItem {
  return {
    id: event.id,
    source: 'event',
    sectionLabel: 'Calendar',
    title: event.title,
    subtitle: buildQuickReminderSubtitle({
      title: event.title,
      date: event.date,
      time: event.time,
      captureKind: event.captureKind,
      reminderStrength: event.reminderStrength,
    }),
    date: event.date,
    emoji: '⏰',
    path: '/calendar',
    daysUntil: 0,
  };
}

export function QuickReminderComposerProvider({ children }: { children: ReactNode }) {
  const { addEvent, user } = useApp();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<ReminderPreset | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [strength, setStrength] = useState<ReminderStrength>('standard');
  const [saving, setSaving] = useState(false);

  const parsed = useMemo(
    () => parseQuickReminderInput(rawInput, { preset: selectedPreset, reminderStrength: strength }),
    [rawInput, selectedPreset, strength],
  );
  const usingCustomDate = selectedPreset === 'custom';

  const resetComposer = useCallback(() => {
    setOpen(false);
    setRawInput('');
    setSelectedPreset(null);
    setConfirming(false);
    setCustomDate('');
    setCustomTime('');
    setRepeatEnabled(false);
    setStrength('standard');
    setSaving(false);
  }, []);

  const openQuickReminder = useCallback((options?: { preset?: ReminderPreset | null }) => {
    setSelectedPreset(options?.preset || null);
    setOpen(true);
    setConfirming(false);
  }, []);

  const handleContinue = useCallback(() => {
    const nextParsed = parseQuickReminderInput(rawInput, {
      preset: selectedPreset,
      reminderStrength: strength,
    });
    setCustomDate(nextParsed.date);
    setCustomTime(nextParsed.time);
    setRepeatEnabled(nextParsed.recurrence.recurrenceType === 'custom');
    setConfirming(true);
  }, [rawInput, selectedPreset, strength]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    const date = usingCustomDate ? customDate : parsed.date;
    const time = usingCustomDate ? customTime : parsed.time;
    const recurrence = repeatEnabled
      ? parsed.recurrence.recurrenceType === 'custom'
        ? parsed.recurrence
        : buildMonthlyRecurrence(date)
      : {
          recurrenceType: 'one-off' as const,
          recurrenceInterval: null,
          recurrenceUnit: null,
          recurrenceDayOfWeek: null,
          recurrenceWeekOfMonth: null,
          recurrenceMonthDay: null,
          summary: 'Does not repeat',
        };

    const event: CalendarEvent = {
      id: crypto.randomUUID(),
      title: parsed.title,
      date,
      time,
      type: parsed.type,
      captureKind: 'quick_reminder',
      reminderStrength: strength,
      recurrenceType: recurrence.recurrenceType,
      recurrenceInterval: recurrence.recurrenceInterval,
      recurrenceUnit: recurrence.recurrenceUnit as CalendarEvent['recurrenceUnit'],
      recurrenceDayOfWeek: recurrence.recurrenceDayOfWeek,
      recurrenceWeekOfMonth: recurrence.recurrenceWeekOfMonth,
      recurrenceMonthDay: recurrence.recurrenceMonthDay,
    };

    try {
      const savedEvent = await addEvent(event);
      let externalSyncState: 'none' | 'synced' | 'error' = 'none';

      if (savedEvent && user) {
        try {
          const integrationSettings = await loadIntegrationSettings(user.id);

          if (integrationSettings.calendar_mode === 'google_calendar' && isGoogleCalendarConfigured()) {
            const syncResult = await upsertAdultingCalendarItem(
              buildQuickReminderCalendarItem(savedEvent),
              integrationSettings.calendar_external_id || null,
            );
            const syncedAt = new Date().toISOString();
            await saveIntegrationSettings(user.id, {
              calendar_mode: 'google_calendar',
              calendar_external_id: syncResult.calendarId,
              calendar_external_name: syncResult.calendarName,
              calendar_external_url: syncResult.calendarUrl,
              calendar_last_synced_at: syncedAt,
              calendar_sync_status: 'synced',
              google_account_email: syncResult.userEmail || integrationSettings.google_account_email || null,
            });
            externalSyncState = 'synced';
          }
        } catch (error) {
          console.error(error);
          externalSyncState = 'error';
          await saveIntegrationSettings(user.id, {
            calendar_sync_status: 'error',
          }).catch((saveError) => console.error(saveError));
        }
      }

      toast({
        title: 'Reminder saved',
        description: externalSyncState === 'synced'
          ? `${parsed.title} is now in your Adulting calendar and Google Calendar.`
          : externalSyncState === 'error'
            ? `${parsed.title} is in Adulting. Google Calendar needs another try.`
            : `${parsed.title} is now sitting in your Adulting calendar.`,
      });
      resetComposer();
    } finally {
      setSaving(false);
    }
  }, [addEvent, customDate, customTime, parsed, repeatEnabled, resetComposer, strength, toast, user, usingCustomDate]);

  return (
    <QuickReminderComposerContext.Provider value={{ openQuickReminder }}>
      {children}
      {open ? (
        <AppBottomSheet onClose={resetComposer} surfaceClassName="max-h-[75vh] overflow-y-auto">
          <AppBottomSheetHeader
            title={confirming ? 'Check Brain Dump' : 'Brain Dump'}
            subtitle={confirming
              ? 'Keep it brutally simple: check the date, repeat, and reminder strength, then save.'
              : 'Type it like you would say it. Adulting will pull out the next useful date.'}
          />
          <div className="space-y-4 px-4 py-4">
            {!confirming ? (
              <>
                <AppFieldGroup label="What do you need to remember?">
                  <AppInput
                    tag="input"
                    value={rawInput}
                    onChange={(event) => setRawInput((event.target as HTMLInputElement).value)}
                    placeholder="Remind me to pay school trip Friday"
                    autoFocus
                  />
                </AppFieldGroup>

                <div className="space-y-2">
                  <AppText fontSize="$xs" color="$colorMuted">Quick timing</AppText>
                  <AppButtonRow>
                    {REMINDER_PRESETS.map((preset) => (
                      <AppButton
                        key={preset.value}
                        size="sm"
                        tone={selectedPreset === preset.value ? 'primary' : 'outline'}
                        onPress={() => setSelectedPreset(preset.value)}
                      >
                        {preset.label}
                      </AppButton>
                    ))}
                  </AppButtonRow>
                </div>

                {rawInput.trim() ? (
                  <div className="rounded-2xl border border-border bg-card px-4 py-3">
                    <AppText fontSize="$xs" color="$colorMuted">Adulting will save</AppText>
                    <AppText fontWeight="700">{parsed.title}</AppText>
                    <AppText fontSize="$sm" color="$colorMuted">{parsed.dateLabel}</AppText>
                    <AppText fontSize="$sm" color="$colorMuted">{parsed.repeatLabel}</AppText>
                  </div>
                ) : null}

                <AppButtonRow justifyContent="flex-end">
                  <AppButton tone="outline" onPress={resetComposer}>
                    Cancel
                  </AppButton>
                  <AppButton onPress={handleContinue} disabled={!rawInput.trim()}>
                    Continue
                  </AppButton>
                </AppButtonRow>
              </>
            ) : (
              <>
                <div className="rounded-2xl border border-border bg-card px-4 py-3">
                  <AppText fontSize="$xs" color="$colorMuted">Title</AppText>
                  <AppText fontWeight="700">{parsed.title}</AppText>
                </div>

                <div className="rounded-2xl border border-border bg-card px-4 py-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <AppText fontSize="$xs" color="$colorMuted">Date and time</AppText>
                      <AppText fontWeight="700">{usingCustomDate ? `${customDate || parsed.date}${customTime ? ` at ${customTime}` : ''}` : parsed.dateLabel}</AppText>
                    </div>
                    <AppButton size="sm" tone={usingCustomDate ? 'primary' : 'outline'} onPress={() => setSelectedPreset(usingCustomDate ? null : 'custom')}>
                      {usingCustomDate ? 'Use parsed' : 'Custom'}
                    </AppButton>
                  </div>
                  {usingCustomDate ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <AppDateField label="Date" value={customDate} onChange={setCustomDate} allowClear={false} />
                      <AppFieldGroup label="Time">
                        <AppInput
                          tag="input"
                          type="time"
                          value={customTime}
                          onChange={(event) => setCustomTime((event.target as HTMLInputElement).value)}
                        />
                      </AppFieldGroup>
                    </div>
                  ) : (
                    <AppButtonRow>
                      {REMINDER_PRESETS.filter((preset) => preset.value !== 'custom').map((preset) => (
                        <AppButton
                          key={preset.value}
                          size="sm"
                          tone={selectedPreset === preset.value ? 'primary' : 'outline'}
                          onPress={() => {
                            setSelectedPreset(preset.value);
                            const nextParsed = parseQuickReminderInput(rawInput, {
                              preset: preset.value,
                              reminderStrength: strength,
                            });
                            setCustomDate(nextParsed.date);
                            setCustomTime(nextParsed.time);
                          }}
                        >
                          {preset.label}
                        </AppButton>
                      ))}
                    </AppButtonRow>
                  )}
                </div>

                <div className="rounded-2xl border border-border bg-card px-4 py-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <AppText fontSize="$xs" color="$colorMuted">Repeat</AppText>
                      <AppText fontWeight="700">{repeatEnabled ? (parsed.recurrence.recurrenceType === 'custom' ? parsed.repeatLabel : 'Repeats every month') : 'Does not repeat'}</AppText>
                    </div>
                    <AppSwitch checked={repeatEnabled} onCheckedChange={setRepeatEnabled} />
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card px-4 py-3 space-y-3">
                  <div>
                    <AppText fontSize="$xs" color="$colorMuted">Reminder strength</AppText>
                    <AppText fontWeight="700">{REMINDER_STRENGTH_OPTIONS.find((option) => option.value === strength)?.label}</AppText>
                  </div>
                  <AppButtonRow>
                    {REMINDER_STRENGTH_OPTIONS.map((option) => (
                      <AppButton
                        key={option.value}
                        size="sm"
                        tone={strength === option.value ? 'primary' : 'outline'}
                        onPress={() => setStrength(option.value)}
                      >
                        {option.label}
                      </AppButton>
                    ))}
                  </AppButtonRow>
                  <AppText fontSize="$sm" color="$colorMuted">
                    {getReminderStrengthDescription(strength)}
                  </AppText>
                </div>

                <AppButtonRow justifyContent="flex-end">
                  <AppButton tone="outline" onPress={() => setConfirming(false)} disabled={saving}>
                    Back
                  </AppButton>
                  <AppButton onPress={() => void handleSave()} disabled={saving || !(usingCustomDate ? customDate : parsed.date)}>
                    {saving ? 'Saving…' : 'Save reminder'}
                  </AppButton>
                </AppButtonRow>
              </>
            )}
          </div>
        </AppBottomSheet>
      ) : null}
    </QuickReminderComposerContext.Provider>
  );
}

export function useQuickReminderComposer() {
  const context = useContext(QuickReminderComposerContext);
  if (!context) {
    throw new Error('useQuickReminderComposer must be used within QuickReminderComposerProvider');
  }
  return context;
}
